import React, { useState, useEffect, useRef } from "react";
import hljs from "highlight.js";
hljs.configure({ ignoreUnescapedHTML: true });
import DOMPurify from "dompurify";
import AudioPlayer from "./AudioPlayer";
import "./MessageBubble.css";

const MessageBubble = ({ message, isLast, autoSpeak, onRetry }) => {
  const { role, content, voice, imageAnalysis, mode, error, blocked } = message || {};
  const safeContent = content || "";
  const [copied, setCopied] = useState(false);
  const [copiedBlock, setCopiedBlock] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const codeRefs = useRef({});

  useEffect(function() {
    Object.values(codeRefs.current).forEach(function(el) {
      if (el && el.textContent) {
        el.removeAttribute("data-highlighted");
        hljs.highlightElement(el);
      }
    });
  }, [safeContent]);

  var handleCopy = async function() {
    try {
      await navigator.clipboard.writeText(safeContent);
      setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
    } catch (err) {
      var ta = document.createElement("textarea"); ta.value = safeContent;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta); setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
    }
  };

  var handleCopyBlock = async function(code, index) {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedBlock(index); setTimeout(function() { setCopiedBlock(null); }, 2000);
    } catch (err) {
      var ta = document.createElement("textarea"); ta.value = code;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta); setCopiedBlock(index); setTimeout(function() { setCopiedBlock(null); }, 2000);
    }
  };

  var handleDownload = function(code, language, index) {
    var extMap = { html:"html", js:"js", python:"py", py:"py", css:"css", bash:"sh", sh:"sh", json:"json", sql:"sql", ts:"ts", rust:"rs", go:"go", c:"c", cpp:"cpp", java:"java", yaml:"yml" };
    var ext = extMap[language] || "txt";
    var blob = new Blob([code], { type: "text/plain" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a"); a.href = url; a.download = "zanox-script-" + index + "." + ext;
    a.click(); URL.revokeObjectURL(url);
  };

  var parseContent = function(text) {
    var parts = [], regex = /```(\w*)\n([\s\S]*?)```/g, last = 0, m;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > last) parts.push({ type: "text", content: text.slice(last, m.index) });
      parts.push({ type: "code", language: m[1] || "plaintext", content: m[2].trim() });
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push({ type: "text", content: text.slice(last) });
    return parts.length > 0 ? parts : [{ type: "text", content: text }];
  };


 var formatText = function(text) {
    return text
      .replace(/\$\$([\s\S]*?)\$\$/g, function(_, m) { return "【 " + m.replace(/\$/g, "").trim() + " 】"; })
      .replace(/\$([^$]+)\$/g, function(_, m) { return "⟨ " + m.trim() + " ⟩"; })
      .replace(/\\Gamma/g, "Γ").replace(/\\Delta/g, "Δ").replace(/\\Theta/g, "Θ")
      .replace(/\\Lambda/g, "Λ").replace(/\\Xi/g, "Ξ").replace(/\\Pi/g, "Π")
      .replace(/\\Sigma/g, "Σ").replace(/\\Phi/g, "Φ").replace(/\\Psi/g, "Ψ").replace(/\\Omega/g, "Ω")
      .replace(/\\alpha/g, "α").replace(/\\beta/g, "β").replace(/\\gamma/g, "γ")
      .replace(/\\delta/g, "δ").replace(/\\epsilon/g, "ε").replace(/\\zeta/g, "ζ")
      .replace(/\\eta/g, "η").replace(/\\theta/g, "θ").replace(/\\iota/g, "ι")
      .replace(/\\kappa/g, "κ").replace(/\\lambda/g, "λ").replace(/\\mu/g, "μ")
      .replace(/\\nu/g, "ν").replace(/\\xi/g, "ξ").replace(/\\pi/g, "π")
      .replace(/\\rho/g, "ρ").replace(/\\sigma/g, "σ").replace(/\\tau/g, "τ")
      .replace(/\\upsilon/g, "υ").replace(/\\phi/g, "φ").replace(/\\chi/g, "χ")
      .replace(/\\psi/g, "ψ").replace(/\\omega/g, "ω")
      .replace(/\\infty/g, "∞").replace(/\\int/g, "∫").replace(/\\sum/g, "∑")
      .replace(/\\prod/g, "∏").replace(/\\sqrt/g, "√").replace(/\\partial/g, "∂")
      .replace(/\\times/g, "×").replace(/\\cdot/g, "·").replace(/\\pm/g, "±")
      .replace(/\\approx/g, "≈").replace(/\\equiv/g, "≡").replace(/\\propto/g, "∝")
      .replace(/\\leq/g, "≤").replace(/\\geq/g, "≥").replace(/\\neq/g, "≠")
      .replace(/\\rightarrow/g, "→").replace(/\\leftarrow/g, "←")
      .replace(/\\Rightarrow/g, "⇒").replace(/\\Leftarrow/g, "⇐")
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "($1/$2)")
      .replace(/\\text\{([^}]+)\}/g, "$1").replace(/\\mathrm\{([^}]+)\}/g, "$1")
      .replace(/\\mathbf\{([^}]+)\}/g, "$1").replace(/\\textit\{([^}]+)\}/g, "$1")
      .replace(/\\begin\{[^}]+\}/g, "").replace(/\\end\{[^}]+\}/g, "")
      .replace(/\\left/g, "").replace(/\\right/g, "")
      .replace(/\\,/g, "").replace(/\\;/g, "").replace(/\\!/g, "")
      .replace(/\_\{([^}]+)\}/g, "_$1").replace(/\^\{([^}]+)\}/g, "^$1")
      .replace(/\\/g, "")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^#### (.+)$/gm, "<h4>$1</h4>")
      .replace(/^### (.+)$/gm, "<h3>$1</h3>")
      .replace(/^## (.+)$/gm, "<h2>$1</h2>")
      .replace(/^# (.+)$/gm, "<h1>$1</h1>")
      .replace(/^---$/gm, "<hr>")
      .replace(/^\* (.+)$/gm, "<li>$1</li>");
  };

  var contentParts = parseContent(safeContent);
  var hasCode = contentParts.some(function(p) { return p.type === "code"; });
  var codeIndex = 0;

  return (
    <div className={"message-row " + (role || "user")}>
      <div className={"message-avatar " + (role || "user")}>
        {role === "user" ? <i className="fas fa-user"></i> : <i className="fas fa-infinity"></i>}
      </div>
      <div className={"message-bubble " + (role || "user") + (error ? " error" : "") + (blocked ? " blocked" : "") + (hasCode ? " has-code" : "")}>
        {voice && <div className="message-meta"><i className="fas fa-microphone"></i><span>Voice</span></div>}
        {imageAnalysis && <div className="message-meta">{mode === "handwriting" ? <i className="fas fa-pen-fancy"></i> : <i className="fas fa-image"></i>}<span>{mode === "handwriting" ? "Handwriting" : "Image"}</span></div>}
        {blocked && <div className="message-meta blocked-meta"><i className="fas fa-shield-halved"></i><span>Reframed</span></div>}

        <div className="message-content">
          {contentParts.map(function(part, index) {
            if (part.type === "code") {
              var ci = codeIndex++;
              return (
                <div key={index} className="code-block-wrapper">
                  <div className="code-block-header">
                    <div className="code-block-dots"><span className="dot red"></span><span className="dot yellow"></span><span className="dot green"></span></div>
                    <span className="code-block-lang">{part.language || "text"}</span>
                    <div className="code-block-actions">
                      <button className="code-action-btn download-btn" onClick={function() { handleDownload(part.content, part.language, ci); }}><i className="fas fa-download"></i><span>Download</span></button>
                      <button className="code-action-btn copy-code-btn" onClick={function() { handleCopyBlock(part.content, ci); }}><i className={"fas " + (copiedBlock === ci ? "fa-check" : "fa-copy")}></i><span>{copiedBlock === ci ? "Copied" : "Copy"}</span></button>
                    </div>
                  </div>
                  <pre className="code-block"><code ref={function(el) { codeRefs.current[ci] = el; }} className={part.language ? "language-" + part.language : ""}>{part.content}</code></pre>
                </div>
              );
            }
            var rawHtml = formatText(part.content).split("\n").map(function(l) { return "<p>" + (l || "\u00A0") + "</p>"; }).join("");
            var cleanHtml = DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS: ["p","strong","em","br","span","h1","h2","h3","h4","h5","h6","hr","ul","ol","li","code","pre","table","thead","tbody","tr","th","td"], ALLOWED_ATTR: ["class"] });
            return <div key={index} className="text-block" dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
          })}
        </div>

        <div className="message-actions">
          <button className="action-btn copy-btn" onClick={handleCopy}><i className={"fas " + (copied ? "fa-check" : "fa-copy")}></i><span>{copied ? "Copied" : "Copy All"}</span></button>
          {role === "zanox" && isLast && onRetry && <button className="action-btn retry-btn" onClick={onRetry}><i className="fas fa-redo"></i><span>Retry</span></button>}
          {contentParts.some(function(p) { return p.type === "code" && (p.language === "html" || p.language === "htm"); }) && <button className="action-btn run-btn" onClick={function() { setShowPreview(showPreview === "main" ? null : "main"); }}><i className={"fas fa-" + (showPreview === "main" ? "eye-slash" : "play")}></i><span>{showPreview === "main" ? "Hide" : "Run Preview"}</span></button>}
          {role === "zanox" && safeContent && <AudioPlayer text={safeContent} autoPlay={isLast ? autoSpeak : false} />}
        </div>
        {showPreview === "main" && contentParts.some(function(p) { return p.type === "code" && (p.language === "html" || p.language === "htm"); }) && (
          <div className="code-preview">
            <div className="code-preview-header"><i className="fas fa-window-maximize"></i><span>Live Preview</span><button className="code-preview-close" onClick={function() { setShowPreview(null); }}><i className="fas fa-times"></i></button></div>
            <iframe className="code-preview-iframe" srcDoc={contentParts.find(function(p) { return p.type === "code"; })?.content || ""} title="Preview" sandbox="allow-scripts" scrolling="yes" />
          </div>
        )}
        <div className="message-time">{message && message.id ? new Date(parseInt(String(message.id).split("-")[0])).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
      </div>
    </div>
  );
};

export default MessageBubble;
